<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="legend-tab">
    <section class="lake-section">
      <ys-text type="title4" class="subtitle">Lake Contours</ys-text>
      <ys-text type="body1" class="eyebrow">
        Contours show lake depth in feet.
      </ys-text>
      <div class="image-container">
        <!-- eslint-disable-next-line vuejs-accessibility/alt-text -->
        <img :src="contours" />
      </div>
    </section>
    <section>
      <ys-text type="title4" class="subtitle">Fishing Areas</ys-text>
      <ys-text type="body1" class="eyebrow">
        Fishing Areas are verified productive areas that are informed by
        verified catches and trusted fishery sources.
      </ys-text>
      <div class="fishing-area-images">
        <div class="image-container areas">
          <!-- eslint-disable-next-line vuejs-accessibility/alt-text -->
          <img :src="fishingAreaNumber" />
        </div>
        <div class="image-container areas">
          <!-- eslint-disable-next-line vuejs-accessibility/alt-text -->
          <img :src="fishingAreaPoint" />
        </div>
      </div>
    </section>
    <section>
      <ys-text type="title4" class="subtitle">Lake Details</ys-text>
      <ys-text type="body1" class="eyebrow">
        View lakebed details including sand, rocks, vegetation, buoys, hazards,
        and more.
      </ys-text>
      <div class="grid-container">
        <div
          v-for="(icon, index) in detailsIcons"
          :key="index"
          class="image-item grid-item lake-details-item"
        >
          <div class="image-container">
            <img :src="icon.icon" :alt="icon.caption" />
          </div>
          <ys-text type="metadata1" class="label">
            {{ icon.caption }}
          </ys-text>
        </div>
      </div>
    </section>
    <section>
      <ys-text type="title4" class="subtitle">Land Areas</ys-text>
      <ys-text type="body1" class="eyebrow">
        Select an area on the map to learn about the managing agency and allowed
        activities.
      </ys-text>
      <div class="image-container">
        <div
          v-for="(landArea, index) in landAreas"
          :key="index"
          class="image-item"
        >
          <img
            :src="landArea.src"
            :title="landArea.label"
            :alt="landArea.label"
          />
          <ys-text type="metadata1" class="label">
            {{ landArea.label }}
          </ys-text>
        </div>
      </div>
    </section>
    <section>
      <ys-text type="title4" class="subtitle">Recreation Sites</ys-text>
      <ys-text type="body1" class="eyebrow">
        Select any recreation site on the map to learn more. Look for the
        following popular recreation sites as you plan your next fishing
        adventure.
      </ys-text>
      <div class="grid-container">
        <div
          v-for="(recPoint, index) in recpointIcons"
          :key="index"
          class="image-item grid-item"
        >
          <span v-html="recPoint.glyph" />
          <ys-text type="metadata1" class="label">
            {{ recPoint.caption }}
          </ys-text>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import '@onxmaps/yellowstone-ui/ys-text'
import { getGlyph } from '@/lib/glyph-markers'
import { GLYPH_ICON_TYPES, GLYPH_FONTS } from '@/lib/glyph-markers-constants'

import fishStateLand from '@/assets/images/legend-images/fish/fish-state-land.png'
import fishFederalLand from '@/assets/images/legend-images/fish/fish-federal-land.png'
import fishTribalLand from '@/assets/images/legend-images/fish/fish-tribal-land.png'
import contours from '@/assets/images/legend-images/fish/contours.png'
import fishingAreaNumber from '@/assets/images/legend-images/fish/fishing-areas-number.jpg'
import fishingAreaPoint from '@/assets/images/legend-images/fish/fishing-areas-point.jpg'

import vegetation from '@/assets/icons/fish/legend/vegetation.svg'
import buoy from '@/assets/icons/fish/legend/buoy.svg'
import hazard from '@/assets/icons/fish/legend/hazard.svg'
import deadfall from '@/assets/icons/fish/legend/deadfall.svg'
import squareBog from '@/assets/icons/fish/legend/square-bog.svg'
import squareEelGrass from '@/assets/icons/fish/legend/square-eelGrass.svg'
import squareEmergentVeg from '@/assets/icons/fish/legend/square-emergentVegetation.svg'
import squareHazard from '@/assets/icons/fish/legend/square-hazard.svg'
import squareSubmergentVeg from '@/assets/icons/fish/legend/square-submergentVegetation.svg'
import squareTimber from '@/assets/icons/fish/legend/square-timber.svg'
import squareWetland from '@/assets/icons/fish/legend/square-wetland.svg'
import squareWildRice from '@/assets/icons/fish/legend/square-wildRice.svg'

///// types /////
interface GlyphIcon {
  caption: string
  glyphName: string
  glyph?: string
}

///// refs and variables /////
const legendIcons: GlyphIcon[] = [
  { caption: 'Fishing Access', glyphName: 'FISHING_ACCESS' },
  { caption: 'Boat Ramp', glyphName: 'BOAT_RAMP' },
  { caption: 'Boat Dock', glyphName: 'BOAT_DOCK' },
  { caption: 'Boat Trailer Parking', glyphName: 'BOAT_TRAILER_PARKING' },
  { caption: 'Carry-In Boat Launch', glyphName: 'HAND_LAUNCH' },
  { caption: 'Canoe Trailhead', glyphName: 'CANOE_TRAILHEAD' },
  { caption: 'Shore Fishing', glyphName: 'SHORE_FISHING' },
  { caption: 'Fishing Pier', glyphName: 'FISHING_PIER' },
  { caption: 'Marina', glyphName: 'MARINA' },
  { caption: 'Pier', glyphName: 'PIER_DOCK' },
  { caption: 'Bait Shop', glyphName: 'BAIT_SHOP' },
  { caption: 'Gauging Station', glyphName: 'GAUGING_STATION' },
  { caption: 'Licensing Agent', glyphName: 'LICENSING_AGENT' },
]

const detailsIcons = [
  { caption: 'Hazards', icon: hazard },
  { caption: 'Buoys', icon: buoy },
  { caption: 'Deadfall', icon: deadfall },
  { caption: 'Miscellaneous Vegetation', icon: vegetation },
  { caption: 'Submergent Vegetation', icon: squareSubmergentVeg },
  { caption: 'Emergent Vegetation', icon: squareEmergentVeg },
  { caption: 'Submergent Timber', icon: squareTimber },
  { caption: 'Eel Grass', icon: squareEelGrass },
  { caption: 'Wild Rice', icon: squareWildRice },
  { caption: 'Wetland Fill', icon: squareWetland },
  { caption: 'Bog', icon: squareBog },
  { caption: 'Hazard or Obstruction', icon: squareHazard },
]

const landAreas = [
  { src: fishFederalLand, label: 'Federal/State/Local Government Lands' },
  { src: fishStateLand, label: 'State Park & National Park' },
  { src: fishTribalLand, label: 'Tribal Government Lands' },
]

///// computed /////
const recpointIcons = computed<GlyphIcon[]>(() =>
  legendIcons.map((icon: GlyphIcon) => {
    return {
      ...icon,
      glyph: getGlyph(icon.glyphName, GLYPH_ICON_TYPES.ROUNDED),
    }
  })
)

const iconFontFamily = computed(() => GLYPH_FONTS.ROUNDED)
</script>

<style lang="scss" scoped>
.legend-tab {
  padding: 0 var(--ys-spacing-200);

  section {
    margin-bottom: var(--ys-spacing-400);
  }

  .title {
    margin-bottom: var(--ys-spacing-200);
    display: block;
  }

  .subtitle,
  .eyebrow {
    margin-bottom: var(--ys-spacing-100);
    display: block;
  }

  .fishing-area-images {
    display: flex;
    gap: var(--ys-spacing-200);
  }

  .label {
    display: block;
  }

  .grid-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    margin: 20px 0;

    .grid-item {
      width: 75px;
      margin-bottom: 25px;
      text-align: center;
      font-family: v-bind(iconFontFamily);
      font-size: 26px;

      &.lake-details-item {
        margin-bottom: 0;
      }
    }
  }

  .image-container {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-evenly;
    margin: 20px 0;
    border-radius: 8px;
    overflow: hidden;

    .image-item {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      text-align: center;
      max-width: 105px;
      margin: 5px 0;
    }

    &.areas {
      flex-basis: 50%;
    }
  }
}
</style>
